import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { deriveIdmeHeroParams } from 'lib/commerce/heroes/heroVerification';
import { cartAttributesForSync } from 'lib/analytics/checkoutAttributes';
import { allowsAds } from 'lib/consent/gate';
import type { Cart, CartLineAttribute } from 'lib/commerce/cart/types';
import { readUrlCartParams } from '../urlCartParams';
import {
  TaxConfigKey,
  deleteFromLocalStorage,
  getFromLocalStorage,
  isCartMutateSuccess,
  saveToLocalStorage,
} from '../utils';

import { CartApiService } from '../services/CartApiService';
import { CartState } from './types';
import { CartHydrationState } from '../types';
import { GiftWrappingProductShopifyMap } from 'content/products/giftWrappingProduct';
import { ClueShopifyMap } from 'content/products/addons/clue';
import {
  SizingKitShopifyMap,
  RingProSizingKitShopifyMap,
} from 'content/products/ring';
import { RareSizingKitShopifyMap } from 'content/products/rare/productMap/constants';
import { SizingKitProductShopifyMap } from 'content/products/accessories/SizingKitProductShopifyMap';

export const useCartState = (): CartState => {
  const [cart, setCartInternal] = useState<Record<string, any> | null>(null);
  const [cartHydrationState, setCartHydrationState] =
    useState<CartHydrationState>('pending');
  const [store, setStoreState] = useState<ShopifyStore | null>(null);
  const [cartLoading, setCartLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const t = useTranslations('cart');

  const urlParams = useRef<CartLineAttribute[] | null>(null);
  const isUpdating = useRef<boolean>(false);
  const cartStore = useRef<ShopifyStore | null>(null);
  const previousStore = useRef<ShopifyStore | null>(null);

  const setCart = useCallback(
    (newCart: Record<string, any> | null) => {
      setCartInternal(newCart);
      cartStore.current = newCart && store ? store : null;
    },
    [store],
  );

  const api = useMemo(
    () => (store ? new CartApiService(store) : null),
    [store],
  );

  const getCart = useCallback(
    async (cartId: string) => {
      if (!api || !cartId) return undefined;
      const data = await api.mutateCart({ cartId });
      if ('error' in data && data.error) return undefined;
      return data;
    },
    [api],
  );

  const applyAttributes = useCallback(
    async (cartId: string, attributes: CartLineAttribute[]) => {
      if (!api || !cartId) return undefined;
      const data = await api.mutateCart({ cartId, cartAttributes: attributes });
      if ('error' in data && data.error) return undefined;
      return data;
    },
    [api],
  );

  const updateCartAttributes = useCallback(
    (cartId: string, attributes: Array<Record<string, string>>): Promise<any> =>
      applyAttributes(cartId, attributes as unknown as CartLineAttribute[]),
    [applyAttributes],
  );

  // Best-effort: merges URL attributes into `fetched`. A failed write still
  // returns that cart with `attempted`, so the caller can show what it fetched.
  const syncUrlAttributes = useCallback(
    async (
      fetched: Cart,
      incoming: CartLineAttribute[] | undefined,
    ): Promise<{ cart: Cart; attempted: boolean }> => {
      try {
        const existing = fetched.attributes ?? [];
        const synced = cartAttributesForSync(existing, incoming, allowsAds());
        const unchanged =
          synced.attributes.length === existing.length &&
          synced.attributes.every((a) =>
            existing.some((e) => e.key === a.key && e.value === a.value),
          );
        if (!synced.shouldWrite || unchanged)
          return { cart: fetched, attempted: false };
        const response = await applyAttributes(fetched.id, synced.attributes);
        if (response && isCartMutateSuccess(response) && response.cart?.id) {
          return { cart: response.cart, attempted: true };
        }
        console.error('Error applying cart attributes from URL params');
      } catch (e) {
        console.error('Error applying cart attributes from URL params:', e);
      }
      return { cart: fetched, attempted: true };
    },
    [applyAttributes],
  );

  const updateCart = useCallback(async () => {
    if (!store) return;
    setCartHydrationState('pending');
    setCart(null);

    const currentStoreCartKey = `cart_${store}`;
    const cartId_ls = getFromLocalStorage(currentStoreCartKey);
    if (!cartId_ls || ['', undefined, null].includes(cartId_ls)) {
      setCartHydrationState('ready');
      return;
    }

    try {
      const cartResponse = await getCart(cartId_ls);
      if (
        !cartResponse ||
        !isCartMutateSuccess(cartResponse) ||
        !cartResponse.cart?.id
      ) {
        deleteFromLocalStorage(currentStoreCartKey);
        setCart(null);
        setCartHydrationState('failed');
        return;
      }

      const synced = await syncUrlAttributes(
        cartResponse.cart,
        urlParams.current ?? undefined,
      );
      setCart(synced.cart);
      setCartHydrationState('ready');
    } catch (e) {
      console.error('Error updating cart:', e);
      deleteFromLocalStorage(currentStoreCartKey);
      setCart(null);
      setCartHydrationState('failed');
    }
  }, [store, getCart, syncUrlAttributes, setCart]);

  const deleteCartFromLocalStorage = useCallback(() => {
    setCart(null);
    if (store) deleteFromLocalStorage(`cart_${store}`);
  }, [store, setCart]);

  const processUrlParameters = useCallback(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    // An ID.me Discountify landing carries only `discount`; tag the hero category too.
    urlParams.current = [
      ...readUrlCartParams(),
      ...deriveIdmeHeroParams(params),
    ];
  }, []);

  useEffect(() => {
    if (isUpdating.current) return;
    processUrlParameters();

    if (cart && cart.id && store) {
      const currentStoreCartKey = `cart_${store}`;
      const cartId_ls = getFromLocalStorage(currentStoreCartKey);
      if (cartId_ls && urlParams.current && urlParams.current.length > 0) {
        isUpdating.current = true;
        getCart(cartId_ls)
          .then(async (cartResponse) => {
            if (
              !cartResponse ||
              !isCartMutateSuccess(cartResponse) ||
              !cartResponse.cart?.id
            ) {
              // Stale cart id — clear only when the cart itself is gone.
              deleteFromLocalStorage(currentStoreCartKey);
              setCart(null);
              return;
            }
            const synced = await syncUrlAttributes(
              cartResponse.cart,
              urlParams.current ?? undefined,
            );
            // Nothing to write: leave the cart already in state. A write
            // (including a failed one) publishes the cart we just fetched.
            if (synced.attempted) setCart(synced.cart);
          })
          .catch((e) => {
            console.error('Error updating cart attributes from URL params:', e);
          })
          .finally(() => {
            isUpdating.current = false;
          });
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (store && previousStore.current !== store) {
      setCart(null);
      isUpdating.current = false;
      previousStore.current = store;
      updateCart();
    }
  }, [store]);

  useEffect(() => {
    processUrlParameters();
    updateCart();
  }, []);

  useEffect(() => {
    if (cart && 'id' in cart && store && cartStore.current === store) {
      saveToLocalStorage(`cart_${store}`, cart.id);
    }
  }, [cart, store]);

  // Paid standalone kit excluded by id, not by omission: outside PRODUCTION
  // RingProSizingKitShopifyMap aliases it, and it is a legit solo buy.
  const nonStandaloneProductIds = useMemo(() => {
    if (!store) return [];
    const paidKitId =
      SizingKitProductShopifyMap[store as ShopifyStore]?.shopifyVariantId;
    return [
      GiftWrappingProductShopifyMap[store as ShopifyStore]?.variantId,
      SizingKitShopifyMap[store as ShopifyStore]?.shopifyVariantId,
      RingProSizingKitShopifyMap[store as ShopifyStore]?.shopifyVariantId,
      RareSizingKitShopifyMap[store as ShopifyStore]?.shopifyVariantId,
      ClueShopifyMap[store as ShopifyStore]?.Year1?.variantId,
    ].filter((id) => Boolean(id) && id !== paidKitId);
  }, [store]);

  // Empty cart is deliberately NOT "only accessories" — .every() would say true.
  const hasOnlyNonStandaloneItems = useMemo(() => {
    const lines = cart?.lines?.edges ?? [];
    if (lines.length === 0) return false;
    return lines.every((edge: { node: { merchandise: { id: string } } }) =>
      nonStandaloneProductIds.includes(edge.node.merchandise.id),
    );
  }, [cart, nonStandaloneProductIds]);

  useEffect(() => {
    if (!cart || !store) return;
    // A redeemed gift card can sit on a cart before anything is added to it.
    const isEmpty =
      (cart.lines?.edges ?? []).length === 0 && !cart.appliedGiftCards?.length;
    if (isEmpty || hasOnlyNonStandaloneItems) deleteCartFromLocalStorage();
  }, [cart, store, hasOnlyNonStandaloneItems, deleteCartFromLocalStorage]);

  const computedValues = useMemo(() => {
    const taxKey = TaxConfigKey[store as ShopifyStore];
    return {
      taxes: cart?.cost?.totalTaxAmount?.amount ?? 0,
      duty: cart?.cost?.totalDutyAmount?.amount ?? 0,
      taxString: taxKey ? t(taxKey) : '',
    };
  }, [cart?.cost, store, t]);

  return {
    cart,
    cartHydrationState,
    setCart,
    store,
    setStore: setStoreState,
    cartLoading,
    setCartLoading,
    error,
    setError,
    deleteCartFromLocalStorage,
    updateCart,
    updateCartAttributes,
    syncUrlAttributes,
    hasOnlyNonStandaloneItems,
    ...computedValues,
  };
};
